import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Clock3, FileText, ImagePlus, LayoutGrid, MapPin, Monitor, Phone, Save, Settings2, Star, Tag, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { TV_ICONS, useAssetUrls } from '../components/EntityForm';
import { Select } from '../components/Select';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { Glyph } from './AppsPage';
import { CATEGORIES, hoursOf, STATUS, type Facility } from './FacilitiesPage';

type Form = Omit<Facility, 'id'>;
const EMPTY: Form = {
  name: '',
  subtitle: null,
  description: null,
  icon: 'star',
  imageAssetId: null,
  openingHours: null,
  location: null,
  contact: null,
  category: null,
  featured: false,
  enabled: true,
};

export function FacilityEditPage() {
  const site = useSite();
  const { id } = useParams();
  return <NeedsSite>{site && id ? <Loader key={`${site.id}/${id}`} site={site} id={id} /> : <PageHeader title="Facility" />}</NeedsSite>;
}

function Loader({ site, id }: { site: Site; id: string }) {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['facilities', site.id],
    queryFn: () => api.get<{ items: Facility[] }>(`/sites/${site.id}/facilities`).then((r) => r.items),
    enabled: id !== 'new',
  });
  if (id === 'new') return <Editor site={site} initial={null} />;
  if (list.isLoading) return <Loading />;
  const f = list.data?.find((x) => x.id === id);
  if (!f)
    return (
      <div className="card empty">
        This facility was not found.{' '}
        <button className="btn small" onClick={() => navigate('/experience/facilities')}>
          Back to Facilities
        </button>
      </div>
    );
  return <Editor site={site} initial={f} />;
}

function Card({ icon, title, sub, children }: { icon: ReactNode; title: string; sub: string; children: ReactNode }) {
  return (
    <div className="card">
      <div className="pkg-head">
        <span className="pkg-head-icon">{icon}</span>
        <span>
          <span className="pkg-head-title">{title}</span>
          <span className="muted" style={{ display: 'block', fontSize: 13 }}>
            {sub}
          </span>
        </span>
      </div>
      <div className="fe-body">{children}</div>
    </div>
  );
}

function Counter({ n, max }: { n: number; max: number }) {
  return <span className="fe-count">{`${n}/${max}`}</span>;
}

function Editor({ site, initial }: { site: Site; initial: Facility | null }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const [f, setF] = useState<Form>(initial ?? EMPTY);
  const [img, setImg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const set = (patch: Partial<Form>) => {
    setF((v) => ({ ...v, ...patch }));
    setDirty(true);
  };
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.upload<{ id: string; url: string }>('/assets', file),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      set({ imageAssetId: a.id });
      setImg(a.url);
    },
  });
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['facilities', site.id] });
    setDirty(false);
    navigate('/experience/facilities');
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name.trim(),
        subtitle: f.subtitle?.trim() || null,
        description: f.description?.trim() || null,
        icon: f.icon,
        imageAssetId: f.imageAssetId,
        openingHours: f.openingHours?.trim() || null,
        location: f.location?.trim() || null,
        contact: f.contact?.trim() || null,
        category: f.category || null,
        featured: f.featured,
        enabled: f.enabled,
      };
      return initial ? api.patch(`/facilities/${initial.id}`, body) : api.post(`/sites/${site.id}/facilities`, body);
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api.delete(`/facilities/${initial!.id}`), onSuccess: done });
  const leave = () =>
    dirty ? ask('Leave without saving? Your changes to this facility will be lost.', () => navigate('/experience/facilities'), { action: 'Leave', danger: true }) : navigate('/experience/facilities');
  const photo = img ?? url(f.imageAssetId);
  const hours = hoursOf(f.openingHours, site.timezone);
  const editable = can('ADMIN');

  return (
    <>
      <div className="crumbs">
        <button type="button" onClick={leave} title="Back">
          <ArrowLeft size={15} />
        </button>
        <button type="button" onClick={leave}>
          Facilities
        </button>
        <ChevronRight size={14} />
        <strong>{initial ? 'Edit facility' : 'New facility'}</strong>
      </div>
      <PageHeader
        title={initial ? `Edit facility · ${initial.name}` : 'New facility'}
        sub="The details and photo guests see on the TV. Saved changes reach all TVs within seconds."
        actions={
          initial &&
          editable && (
            <button className="btn danger-outline" onClick={() => ask(`Delete ${initial.name}? It disappears from the TVs.`, () => remove.mutate())}>
              <Trash2 size={16} /> Delete
            </button>
          )
        }
      />
      <ErrorBox error={save.error ?? remove.error} />

      <div className="fe-layout">
        <div className="stack">
          <Card icon={<LayoutGrid size={22} />} title="Basic information" sub="The name, short line and category of this facility.">
            <label className="camp-field">
              <span className="camp-label">
                Facility name <span style={{ color: '#dc2626' }}>*</span>
              </span>
              <input value={f.name} maxLength={80} disabled={!editable} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Swimming Pool" autoFocus={!initial} />
              <Counter n={f.name.length} max={80} />
            </label>
            <label className="camp-field">
              <span className="camp-label">
                Short description <span className="muted">(shown on the facility card)</span>
              </span>
              <input value={f.subtitle ?? ''} maxLength={120} disabled={!editable} onChange={(e) => set({ subtitle: e.target.value })} placeholder="e.g. Relax and enjoy" />
              <Counter n={(f.subtitle ?? '').length} max={120} />
            </label>
            <div className="camp-grid" style={{ marginTop: 0 }}>
              <label className="camp-field">
                <span className="camp-label">Category</span>
                <span className="icon-select">
                  <Tag size={16} />
                  <Select value={f.category ?? ''} disabled={!editable} onChange={(e) => set({ category: e.target.value || null })} aria-label="Category">
                    <option value="">No category</option>
                    {Object.keys(CATEGORIES).map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                    {f.category && !CATEGORIES[f.category] && <option>{f.category}</option>}
                  </Select>
                </span>
              </label>
              <label className="camp-field">
                <span className="camp-label">Icon on the TV</span>
                <span className="icon-select">
                  <Glyph name={f.icon} size={16} />
                  <Select value={f.icon} disabled={!editable} onChange={(e) => set({ icon: e.target.value })} aria-label="Icon">
                    {TV_ICONS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </Select>
                </span>
              </label>
            </div>
          </Card>

          <Card icon={<FileText size={22} />} title="Description" sub="The text on the facility's page on the TV.">
            <label className="camp-field">
              <textarea
                value={f.description ?? ''}
                maxLength={4000}
                disabled={!editable}
                onChange={(e) => set({ description: e.target.value })}
                style={{ minHeight: 130 }}
                placeholder="e.g. Take a refreshing dip in our outdoor infinity pool, overlooking the sea."
              />
              <Counter n={(f.description ?? '').length} max={4000} />
            </label>
          </Card>

          <Card icon={<Settings2 size={22} />} title="Details" sub="Practical information for guests.">
            <div className="camp-grid" style={{ marginTop: 0 }}>
              <label className="camp-field">
                <span className="camp-label">Opening hours</span>
                <span className="fe-icon-input">
                  <Clock3 size={16} />
                  <input value={f.openingHours ?? ''} maxLength={200} disabled={!editable} onChange={(e) => set({ openingHours: e.target.value })} placeholder="Daily 7:00 AM – 8:00 PM" />
                </span>
                <span className="camp-hint">
                  Also “24 hours” or “On request”. Used for the status: <strong>{STATUS[hours.status].label}</strong>.
                </span>
              </label>
              <label className="camp-field">
                <span className="camp-label">Location</span>
                <span className="fe-icon-input">
                  <MapPin size={16} />
                  <input value={f.location ?? ''} maxLength={200} disabled={!editable} onChange={(e) => set({ location: e.target.value })} placeholder="Ground floor, beach side" />
                </span>
              </label>
              <label className="camp-field">
                <span className="camp-label">
                  Contact <span className="muted">(optional)</span>
                </span>
                <span className="fe-icon-input">
                  <Phone size={16} />
                  <input value={f.contact ?? ''} maxLength={120} disabled={!editable} onChange={(e) => set({ contact: e.target.value })} placeholder="Ext. 2100" />
                </span>
              </label>
            </div>
          </Card>
        </div>

        <div className="stack fe-side">
          <Card icon={<ImagePlus size={22} />} title="Photo" sub="Shown on the facility card and page on the TV.">
            <button type="button" className="fe-photo" disabled={!editable || upload.isPending} onClick={() => input.current?.click()}>
              {photo ? (
                <img src={photo} alt="" />
              ) : (
                <span className="fe-photo-empty">
                  <ImagePlus size={28} />
                  <strong>{upload.isPending ? 'Uploading…' : 'Click to add a photo'}</strong>
                  <span>JPG, PNG or WebP</span>
                </span>
              )}
            </button>
            <span className="camp-hint">Recommended: 1280 × 720 or larger, landscape (16:9).</span>
            {editable && photo && (
              <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
                <button className="btn" disabled={upload.isPending} onClick={() => input.current?.click()}>
                  <Upload size={15} /> {upload.isPending ? 'Uploading…' : 'Replace photo'}
                </button>
                <button
                  className="btn danger-outline"
                  onClick={() => {
                    set({ imageAssetId: null });
                    setImg(null);
                  }}
                >
                  <Trash2 size={15} /> Remove
                </button>
              </div>
            )}
            <input
              ref={input}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = '';
              }}
            />
            <ErrorBox error={upload.error} />
          </Card>

          <Card icon={<Settings2 size={22} />} title="Options" sub="How this facility appears on the TV.">
            <div className="fe-options">
              <div>
                <Toggle on={f.enabled} onChange={(v) => editable && set({ enabled: v })} label="Show on TV" />
                <Monitor size={18} />
                <span>
                  <strong>Show on TV</strong>
                  <span className="muted">Guests see this facility in Hotel Facilities.</span>
                </span>
              </div>
              <div>
                <Toggle on={f.featured} onChange={(v) => editable && set({ featured: v })} label="Featured" />
                <Star size={18} />
                <span>
                  <strong>Featured</strong>
                  <span className="muted">Listed first on the TV.</span>
                </span>
              </div>
            </div>
          </Card>

          {editable && (
            <div className="card row" style={{ justifyContent: 'space-between' }}>
              <button className="btn" onClick={leave}>
                Cancel
              </button>
              <button className="btn primary" disabled={!f.name.trim() || save.isPending || upload.isPending} onClick={() => save.mutate()}>
                <Save size={16} /> {save.isPending ? 'Saving…' : initial ? 'Save changes' : 'Add facility'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
